import React from 'react';

type WithIdAndName = { id: string; name?: unknown };

interface TypedNavigationPathItem<T extends WithIdAndName> {
    /** The ID from the original type T. */
    id: T['id'];
    /** The name from the original type T. */
    name: T['name'];
}

type DefaultNavigationPathItem = {
    id: null;
    name?: string;
};

export type NavigationPathItem<T extends WithIdAndName> = TypedNavigationPathItem<T> | DefaultNavigationPathItem;

export interface UseNavigationOptions<T extends WithIdAndName> {
    /**
     * The initial path for the navigation.
     * If empty or not provided, defaultRootItem or a predefined root will be used.
     */
    initialPath?: NavigationPathItem<T>[];
}

export interface UseNavigationResult<T extends WithIdAndName> {
    /** Current navigation path (array of `NavigationPathItem<T>`). */
    path: NavigationPathItem<T>[];
    /** Current active item (last item in `path`). */
    currentItem: NavigationPathItem<T>;
    /** ID of the `currentItem` (type `T`). */
    currentItemId: NavigationPathItem<T>['id'];
    /** Appends item (must have `id`, `name`) to path. */
    navigateToItem: (item: NavigationPathItem<T>) => void;
    /** Navigates to item at `index`, truncating path. */
    navigateBackToIndex: (index: number) => void;
    /** Resets path to its initial state. */
    resetNavigation: () => void;
    /** Directly sets the navigation path (React state setter). Use cautiously. */
    setPath: React.Dispatch<React.SetStateAction<NavigationPathItem<T>[]>>;
}

const DEFAULT_ID = null;
const DEFAULT_NAME = '';

/**
 * Custom hook for managing hierarchical navigation state.
 * It handles the current path and provides functions to navigate deeper,
 * go back to a specific point, reset the path, or set it directly.
 *
 * @param {UseNavigationOptions<T>}
 * @returns {UseNavigationResult<T>}
 */
export const useNavigation = <T extends WithIdAndName>({
    initialPath: providedInitialPath,
}: UseNavigationOptions<T>): UseNavigationResult<T> => {
    const resolvedInitialPath = React.useMemo((): NavigationPathItem<T>[] => {
        if (providedInitialPath && providedInitialPath.length > 0) {
            return providedInitialPath;
        }

        return [{ id: DEFAULT_ID, name: DEFAULT_NAME }];
    }, [providedInitialPath]);

    const [path, setPath] = React.useState<NavigationPathItem<T>[]>(resolvedInitialPath);

    const currentItem = path[path.length - 1];
    const currentItemId = currentItem.id;

    /**
     * Appends an item to the navigation path.
     *
     * @param {NavigationPathItem<T>} item
     */
    const navigateToItem = React.useCallback(
        (item: NavigationPathItem<T>) => setPath((prevPath) => [...prevPath, item]),
        [],
    );

    /**
     * Navigates back to a previous item by its index, effectively truncating the path.
     *
     * @param {number} index
     */
    const navigateBackToIndex = React.useCallback((index: number) => {
        setPath((currentPath) => {
            if (index >= 0 && index < currentPath.length - 1) {
                return currentPath.slice(0, index + 1);
            }
            return currentPath;
        });
    }, []);

    /**
     * Resets the navigation path to its initially resolved state.
     */
    const resetNavigation = React.useCallback(() => setPath(resolvedInitialPath), [resolvedInitialPath]);

    return {
        path,
        currentItem,
        currentItemId,
        navigateToItem,
        navigateBackToIndex,
        resetNavigation,
        setPath,
    };
};
